import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  MARGEN_SUGERIDO_BP,
  costoDeUnidadVenta,
  formatBp,
  formatMoney,
  parseMoney,
  precioParaMargen,
  type Centavos,
} from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { costoConocido } from '../lib/costos.js';
import { emitir } from '../lib/emitir.js';
import { usePuede } from '../lib/sesion.js';
import { coincideProducto, etiquetaListaPrecio, listaPredeterminada, ordenarListas, precioDe, unidadesDe } from '../lib/catalogo.js';
import { CorregirCostoInventario } from '../ui/CorregirCostoInventario.js';
import { MargenDe } from '../ui/Margen.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { useNombreProducto } from '../i18n/productos.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';

type Filtro = 'precio' | 'costo' | 'todos';

/**
 * Pricing a whole catalogue in one sitting.
 *
 * The starter catalogue lands 140 products with no prices, deliberately — a seeded price is a
 * wrong price somebody will trust. That was the right call and it left a hole: the only way to
 * price them was to open each product in turn, which is 140 round trips through a detail screen.
 *
 * So this screen is one row per product and nothing else. Where a cost is known the suggestion is
 * one tap, and the arithmetic nobody wants to do 140 times stops being the reason this never gets
 * finished.
 *
 * Each row saves with its own «Guardar» (the owner, 2026-10-18). A price used to commit when its box
 * lost focus, and a row that no longer lacked anything left «Sin precio» there and then: «it just
 * disappears if i fill out the form and it sucks to have to search for the product again if i made a
 * typo». Now nothing is saved until the row's «Guardar», or Enter in one of its boxes, and a row saved
 * stays where it is, «Guardado», until another view is picked: a typo is put right in place. A cost
 * set from a row keeps it there the same way.
 *
 * Each row has a box for every price list, side by side: the retail price beside the wholesale
 * one, which is easier to see than a list picked above the rows (the owner, 2026-10-05).
 *
 * «Precios/Costos» since 2026-10-18 (the owner): as it lists the products still missing a price,
 * it lists those missing a cost, «Sin costo», the stock counted in at nothing whose sales look all
 * profit, and each row's «Costo» opens the product's «Corregir el costo» right here, for the owner
 * to go down the list instead of through each product's page.
 *
 * Under each price, for whoever sees costs, the margin it leaves (the owner, 2026-11-03: «can we
 * also see the margin we're making on each product card?»).
 */
export function Precios() {
  const t = useT();
  const nombre = useNombreProducto();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  /**
   * What a product costs on average, and the price that cost suggests, are the books': the owner's
   * to see. A cashier keeps the prices too since 2026-10-09, without them, as on a product's page.
   */
  const verCostos = usePuede('ver_costos');
  /** What the stock costs, set by hand: the owner's (2026-10-13), as on a product's page. */
  const corregirCosto = usePuede('configurar') && verCostos;
  /** For what a product cost the last time it was bought, when no shelf holds it at a cost now. */
  const compras = useLiveQuery(() => (verCostos ? db.compras.toArray() : []), [verCostos], []);

  /** The default list first: the retail price, then the wholesale one, then any other the shop has. */
  const activas = ordenarListas(listas);
  const predeterminada = listaPredeterminada(activas);
  const columnas = predeterminada ? [predeterminada, ...activas.filter((l) => l.id !== predeterminada.id)] : activas;
  /** Missing a price, missing a cost, or every product. */
  const [filtro, setFiltro] = useState<Filtro>('precio');
  /** The product whose cost is being set, by id, so the sheet follows what the books say of it. */
  const [costeando, setCosteando] = useState<string | null>(null);
  const [consulta, setConsulta] = useState('');
  /** What is in the boxes right now, keyed by sale unit and list. Absent means "show what is saved". */
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  /**
   * The products saved on this visit. They stay under «Sin precio» or «Sin costo» once they lack
   * nothing, until another view is picked, so that a typo is put right where it was made.
   */
  const [quedan, setQuedan] = useState<ReadonlySet<string>>(() => new Set());
  /** Those whose cost was set on this visit: setting it again is putting the first right. */
  const [costeados, setCosteados] = useState<ReadonlySet<string>>(() => new Set());
  /** The row being saved, against a second tap while the first is on its way. */
  const [guardando, setGuardando] = useState<string | null>(null);

  /**
   * One row per product on sale, priced in its first presentation, with what each list asks for it.
   * One still to price is one with a list that has no price for it: a wholesale client would get
   * its line at nothing at the till, as a walk-in customer would without the retail one.
   */
  const todas = useMemo(
    () =>
      productos
        .filter((p) => p.activo)
        .map((p) => {
          const unidad = unidadesDe(unidades, p.id)[0];
          const guardados = columnas.map((l) => (unidad ? precioDe(precios, unidad.id, l.id) : null));
          // Where it is known, as the till and the home screen's margin warning take it: with nothing on
          // the shelf, the stock's own cost is nothing, and there was no margin and no price to suggest.
          const conocido = costoConocido(p.id, 'tienda', existencias, compras);
          const costo = verCostos && unidad && conocido > 0 ? costoDeUnidadVenta(conocido, unidad.factor) : null;
          return { producto: p, unidad, guardados, costo, falta: guardados.some((g) => g === null), sinCosto: verCostos && conocido === 0 };
        })
        .filter((f) => f.unidad !== undefined),
    [productos, unidades, precios, existencias, compras, columnas.map((l) => l.id).join(), verCostos],
  );
  const filas = todas
    .filter((f) => filtro === 'todos' || quedan.has(f.producto.id) || (filtro === 'precio' ? f.falta : f.sinCosto))
    .filter((f) => !consulta.trim() || coincideProducto(f.producto, consulta))
    .sort((a, b) => nombre(a.producto).localeCompare(nombre(b.producto), localeActual()));

  /** Progress is over the whole catalogue, not over what the filter happens to be showing. */
  const total = todas.length;
  const hechos = todas.filter((f) => !f.falta).length;
  /**
   * «Sin costo» is the books', as the costs on the rows are. Each view of what is lacking says how
   * many lack it, «Sin precio (12)» (the owner, 2026-10-18): over the whole catalogue, as the bar is,
   * and as the books have it now, so a row saved goes off the count though it stays in sight.
   */
  const filtros: readonly { vista: Filtro; etiqueta: Clave; cuantos?: number }[] = [
    { vista: 'precio', etiqueta: 'precios.soloFaltantes', cuantos: total - hechos },
    ...(verCostos ? [{ vista: 'costo' as const, etiqueta: 'precios.soloSinCosto' as const, cuantos: todas.filter((f) => f.sinCosto).length }] : []),
    { vista: 'todos', etiqueta: 'precios.todos' },
  ];
  const enHoja = costeando ? productos.find((p) => p.id === costeando) : undefined;

  /** Another view: what was saved on this one goes where it now belongs. */
  function verOtra(v: Filtro) {
    setFiltro(v);
    setQuedan(new Set());
  }

  /**
   * A box as «Guardar» reads it: nothing to save, a new price, or what is not a price. One emptied is
   * nothing to save: a price is never taken off, and left, the box shows it again.
   */
  function leerCaja(clave: string, guardado: Centavos | null): 'igual' | 'mal' | Centavos {
    const texto = borrador[clave];
    if (texto === undefined || texto.trim() === '') return 'igual';
    const precio = parseMoney(texto);
    if (precio === null || precio <= 0) return 'mal';
    return precio === guardado ? 'igual' : precio;
  }

  /**
   * A row's «Guardar»: a `precio.fijado` for each box that says a new price, and the row kept where it
   * is (`quedan`). Saved, its boxes show what the books hold again: a draft left in one, over what
   * another phone set afterwards, sent the old price again the next time (2026-10-13).
   */
  async function guardar(productoId: string, unidadVentaId: string, nuevos: readonly { listaId: string; precio: Centavos }[]) {
    if (nuevos.length === 0 || guardando) return;
    setGuardando(productoId);
    try {
      for (const n of nuevos) await emitir('precio.fijado', { unidadVentaId, listaId: n.listaId, precio: n.precio });
      setBorrador((b) => Object.fromEntries(Object.entries(b).filter(([clave]) => !clave.startsWith(`${unidadVentaId}:`))));
      setQuedan((q) => new Set(q).add(productoId));
    } finally {
      setGuardando(null);
    }
  }

  if (columnas.length === 0 || total === 0) {
    return (
      <Pagina titulo={t('precios.titulo')}>
        <Vacio mensaje={t('precios.nadaQueMostrar')}>
          <Link to="/productos/semilla" className="btn-primario px-5">
            <Ico icono="semilla" />
            {t('productos.empezarCatalogo')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('precios.titulo')}
      subtitulo={t('precios.subtitulo', { hechos, total })}
      acciones={
        // The catalogue is the tab beside this one. The lists are what these prices are for.
        <Link to="/listas-precio" className="btn-secundario gap-1.5 px-4 text-sm">
          <Ico icono="precios" className="size-4" />
          {t('listas.titulo')}
        </Link>
      }
    >
      <p className="mb-4 rounded-xl bg-slate-900 px-4 py-3 text-xs text-slate-400">
        {verCostos
          ? t('precios.explicacion', { margen: MARGEN_SUGERIDO_BP / 100, minimo: formatBp(config.precioMargenMinimoBp) })
          : t('precios.explicacionSinCosto')}
      </p>

      {/* How far along, so the job has a visible end. */}
      <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full bg-marca-500 transition-all"
          style={{ width: `${total === 0 ? 0 : Math.round((hechos / total) * 100)}%` }}
        />
      </div>

      <div className="mb-4 flex flex-col gap-3">
        {/* Three ways to look don't fit beside the search on a phone: under it there, across. */}
        <div className={`flex gap-2 ${filtros.length > 2 ? 'flex-col sm:flex-row' : ''}`}>
          <input
            className="campo flex-1"
            placeholder={t('precios.buscar')}
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            inputMode="search"
          />
          <div
            className="flex shrink-0 gap-1 rounded-xl bg-slate-800 p-1"
            data-guia="precios-filtro"
            data-todos={filtro === 'todos' ? '' : undefined}
          >
            {filtros.map(({ vista, etiqueta, cuantos }) => (
              <button
                key={vista}
                type="button"
                onClick={() => verOtra(vista)}
                aria-pressed={filtro === vista}
                data-precios-filtro={vista}
                data-sin-icono="pestana"
                // Too narrow for both on one line, the count goes under its name, never into it.
                className={`min-h-10 flex-auto rounded-lg px-2.5 py-1 text-sm leading-tight transition max-[359px]:px-2 ${
                  filtro === vista ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                }`}
              >
                <span className="whitespace-nowrap">{t(etiqueta)}</span>
                {cuantos !== undefined && (
                  <>
                    {' '}
                    <span className="tabular whitespace-nowrap">({cuantos})</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filas.length === 0 ? (
        <Vacio mensaje={t(filtro === 'costo' ? 'precios.todosConCosto' : 'precios.todosListos')}>
          <Link to="/" className="btn-primario px-5">
            <Ico icono="listo" />
            {t('precios.seguir')}
          </Link>
        </Vacio>
      ) : (
        <ul className="flex flex-col gap-2" data-guia="precios-lista">
          {filas.map(({ producto, unidad, guardados, costo, sinCosto }) => {
            const sugerido = costo !== null && costo > 0 ? precioParaMargen(costo, MARGEN_SUGERIDO_BP) : null;
            const cajas = columnas.map((lista, i) => {
              const clave = `${unidad!.id}:${lista.id}`;
              const guardado = guardados[i] ?? null;
              return { lista, clave, guardado, leido: leerCaja(clave, guardado) };
            });
            const nuevos = cajas.flatMap((c) => (typeof c.leido === 'number' ? [{ listaId: c.lista.id, precio: c.leido }] : []));
            const hayMal = cajas.some((c) => c.leido === 'mal');
            const listo = nuevos.length > 0 && !hayMal && guardando !== producto.id;
            /** Saved on this visit, and nothing typed since. */
            const yaGuardado = quedan.has(producto.id) && nuevos.length === 0 && !hayMal;
            const guardarFila = () => {
              if (listo) void guardar(producto.id, unidad!.id, nuevos);
            };
            return (
              <li key={producto.id} className="tarjeta flex flex-col gap-3 p-3" data-precios-producto={producto.id}>
                <div className="flex items-center gap-3">
                  <Miniatura producto={producto} tamano="sm" className="max-[319px]:hidden" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{nombre(producto)}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {t('precios.porUnidad', {
                        unidad: etiquetaUnidad(unidad!.nombre, unidad!.nombre),
                      })}
                      {/* The owner's is in its box beside the name, said once. */}
                      {verCostos && !corregirCosto && ' · '}
                      {verCostos &&
                        !corregirCosto &&
                        (costo !== null && costo > 0
                          ? t('precios.costo', { monto: formatMoney(costo, { symbol: false }) })
                          : t('precios.sinCosto'))}
                    </p>
                  </div>
                  {/*
                    What it costs, in a box that opens «Corregir el costo»: beside the prices' boxes and
                    read like them, marked as an empty one when the books know no cost.
                  */}
                  {corregirCosto && (
                    <button
                      type="button"
                      className={`flex h-12 min-w-18 shrink-0 flex-col items-end justify-center rounded-xl px-3 transition hover:bg-slate-700 ${
                        sinCosto ? 'bg-slate-900 ring-1 ring-amber-700/60 ring-inset' : 'bg-slate-800'
                      }`}
                      aria-label={t('precios.ponerCostoDe', { producto: nombre(producto) })}
                      data-precios-costo={producto.id}
                      onClick={() => setCosteando(producto.id)}
                    >
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Ico icono="editar" className="size-3" />
                        {t('precios.ponerCosto')}
                      </span>
                      <span className={`tabular text-sm font-medium ${sinCosto ? 'text-amber-300' : ''}`}>
                        {costo !== null && costo > 0 ? formatMoney(costo, { symbol: false }) : '—'}
                      </span>
                    </button>
                  )}
                </div>
                {/* A box for each list, side by side; a third list and on wrap under them. */}
                <div className={`grid gap-2 ${columnas.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {cajas.map(({ lista, clave, guardado, leido }) => {
                    const valor = borrador[clave] ?? (guardado === null ? '' : String(guardado / 100));
                    /** The price the box shows, saved or just typed: the margin under it is of this one. */
                    const enCaja = parseMoney(valor);
                    return (
                      <div key={lista.id} className="flex min-w-0 flex-col gap-1">
                        <label className="flex min-w-0 flex-col gap-1">
                          <span className="truncate text-xs text-slate-400">{etiquetaListaPrecio(lista)}</span>
                          <input
                            // Not a price, in red; a new one not saved yet, lit; none at all, marked.
                            className={`campo tabular h-12 min-h-0 w-full min-w-0 px-3 text-right text-lg ${
                              leido === 'mal'
                                ? 'border-rose-500'
                                : typeof leido === 'number'
                                  ? 'border-marca-500'
                                  : guardado === null
                                    ? 'border-amber-700/60'
                                    : ''
                            }`}
                            inputMode="decimal"
                            placeholder="0.00"
                            aria-label={t('precios.campo', { lista: etiquetaListaPrecio(lista), producto: nombre(producto) })}
                            aria-invalid={leido === 'mal'}
                            value={valor}
                            onChange={(e) => setBorrador((b) => ({ ...b, [clave]: e.target.value }))}
                            // Emptied and left, it shows the price it has again: none is ever taken off.
                            onBlur={() => {
                              if (borrador[clave]?.trim() === '') setBorrador(({ [clave]: _, ...resto }) => resto);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') guardarFila();
                            }}
                          />
                        </label>
                        {/*
                          What the price in the box leaves over what one costs, as it is typed: «Margen 28.6%»,
                          amber under the shop's minimum, red when it makes nothing; «—» with no price or no
                          cost known. Under the box, at the right as the price in it is.
                        */}
                        {verCostos && (
                          <p className="flex items-center justify-end gap-1.5 text-xs text-slate-400" data-precios-margen={lista.id}>
                            {t('precios.margen')}
                            <MargenDe
                              precio={enCaja !== null && enCaja > 0 ? enCaja : null}
                              costo={costo}
                              minimoBp={config.precioMargenMinimoBp}
                            />
                          </p>
                        )}
                        {sugerido !== null && guardado === null && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 self-start rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs text-teal-300 transition hover:bg-slate-700 hover:text-teal-200"
                            title={t('precios.sugerido', { monto: formatMoney(sugerido) })}
                            // Into the box, to be saved with the rest of the row.
                            onClick={() => setBorrador((b) => ({ ...b, [clave]: String(sugerido / 100) }))}
                          >
                            <Ico icono="precios" className="size-3.5" />
                            {t('precios.usarSugerido', { monto: formatMoney(sugerido, { symbol: false }) })}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Lit once something new is typed; «Guardado» once it is in the books, until it changes again. */}
                <button
                  type="button"
                  className={`${nuevos.length > 0 ? 'btn-primario' : 'btn-secundario'} h-11 min-h-0 w-full gap-1.5 text-sm`}
                  disabled={!listo}
                  onClick={guardarFila}
                  data-precios-guardar={yaGuardado ? 'guardado' : nuevos.length > 0 ? 'pendiente' : 'nada'}
                >
                  <Ico icono={yaGuardado ? 'listo' : 'guardar'} className="size-4" />
                  {t(yaGuardado ? 'precios.guardado' : 'comun.guardar')}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {enHoja && (
        <CorregirCostoInventario
          producto={enHoja}
          existencias={existencias.filter((e) => e.productoId === enHoja.id)}
          // Set already on this visit, it is that one being put right.
          {...(costeados.has(enHoja.id) ? { motivoInicial: t('costoInv.motivoRecienPuesto') } : {})}
          onCorregido={() => {
            setQuedan((q) => new Set(q).add(enHoja.id));
            setCosteados((c) => new Set(c).add(enHoja.id));
          }}
          onCerrar={() => setCosteando(null)}
        />
      )}
    </Pagina>
  );
}

export type { Centavos };
